import { mapActions, mapGetters } from 'vuex'
import AreaTree from '@/applications/sales/components/AreaTree/AreaTree.vue'
import Configuration from '@/agilis/util/Configuration'
import L from 'lodash'
import { i18n } from '@/i18n/i18n'
import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'
import InlineEditor from '@/agilis/components/InlineEditor/InlineEditor.vue'
import jsonLogic from '@/plugins/jsonLogic'
import DataTableGroupingMixin from '@/agilis/components/DataTableWithGroups/common/DataTableGroupingMixin'
import WorkPackageV3Mixin from '@/applications/sales/components/WorkPackageV3/common/WorkPackageV3Mixin'
import Vue from 'vue'

import FormSelectPriceItem from './FormSelectPriceItem/FormSelectPriceItem.vue'

// Scale factor util to update listPrice depending on the jobType (for internal product only)
function getPriceScaleFactor (priceItem, jobType) {
  let multiplier

  switch (jobType) {
    case 'Festivo / Notturno':
      multiplier = priceItem?.productPricePercentMultiplerNonWorkingDay || 0
      break

    case 'OOH':
      multiplier = priceItem?.productPricePercentMultiplerOutOfOffice || 0
      break

    default:
      multiplier = 0
  }

  const scaleFactor = 1 + (multiplier / 100)

  return scaleFactor
}

function isAllObjDimensionsSelected (obj, dimensions) {
  if (dimensions) {
    const filterDimensions = dimensions.filter(dimension => dimension.filter !== false)
    const filterDimensionsStatus = filterDimensions.map(dimension => {
      return {
        dimension: dimension.dimension,
        selected: !!(obj && L.get(obj, dimension.wpPath))
      }
    })
    return filterDimensionsStatus.every(dimension => dimension.selected === true)
  }
  return false
}

// Trigger used to toggle the external product section with the checkbox
const SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC = {
  jsonLogic: {
    '!!': { var: 'externalWorkpackage' }
  }
}

// Trigger to disable fields before selecting the priceItem
const DISABLED_WHEN_PRICEITEM_NOT_SET = {
  jsonLogic: {
    '!': { var: 'priceItem.id' }
  }
}

export default {
  name: 'WorkPackageV3',
  mixins: [toursMixin, DataTableGroupingMixin, WorkPackageV3Mixin],

  props: {
    configuration: Object,
    selectedContext: Object,
    trackerDTO: Object
  },

  components: {
    InlineEditor
  },

  data: () => ({
    formCustomComponents: {
      AreaTree,
      FormSelectPriceItem
    },
    dialog: false,
    dialogMultiple: false,
    dialogMultipleReadOnly: false,
    tmp: {},
    dialogDelete: false,
    items: [],
    priceItems: {},
    editedIndex: -1,
    editedItem: {
      areaId: null,
      title: '',
      areaName: '',
      description: '',
      externalCost: null,
      externalCostNegotiated: null,
      externalWorkpackage: false,
      externalPriceItem: null,
      quantity: 1,
      supplier: null,
      externalSupplier: null,
      externalSorintBu: null,
      priceItem: null,
      jobType: null,
      cost: null,
      listPrice: null,
      unitPrice: null,
      city: null,
      startDate: null,
      sorintBu: null,
      endDate: null,
      sequence: 1,
      wpType: null,
      currency: null,
      groupId: null,
      tags: []
    },
    defaultItem: {
      areaId: null,
      areaName: '',
      title: '',
      description: '',
      supplier: null,
      externalCost: null,
      externalListPrice: null,
      externalWorkpackage: false,
      externalSupplier: null,
      externalSorintBu: null,
      externalPriceItem: null,
      quantity: 1,
      unitOfMeasure: null,
      priceItem: null,
      jobType: null,
      cost: null,
      listPrice: null,
      unitPrice: null,
      city: null,
      startDate: null,
      sorintBu: null,
      endDate: null,
      sequence: 1,
      wpType: null,
      currency: null,
      groupId: null,
      tags: []
    },
    formRows: [],
    createMultiple: {},
    createMultipleFormRows: [],
    editMode: false,
    editPrice: false,
    editExternalPrice: false
  }),

  computed: {
    ...mapGetters({
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      workspaceId: 'contextStore/workspaceId',
      loading: 'loaderStore/loading'
    }),

    computeHeaders () {
      const hh = [
        { text: this.$t('sales.label.wpTitle'), value: 'title', sortable: false },
        { text: this.$t('sales.label.wpProfile'), value: 'product.name', sortable: false },
        { text: this.$t('sales.label.wpJobType'), value: 'jobType', sortable: false },
        { text: this.$t(this.getQuantityTitleWithType()), value: 'quantity', align: 'end', sortable: false },
        { text: this.$t('sales.label.supplier'), value: 'supplier.title', sortable: false },
        { text: this.$t('sales.label.wpExternalSupplier'), value: 'externalSupplier', sortable: false },
        { text: this.$t('sales.label.wpCost'), value: 'cost', align: 'end', sortable: false },
        { text: this.$t('sales.label.wpListPrice'), value: 'listPrice', align: 'end', sortable: false },
        { text: this.$t('sales.label.wpUnitPrice'), value: 'unitPrice', align: 'end', sortable: false },
        this.priceGuidance ? { text: this.$t('sales.label.wpPriceGuidance'), value: 'priceGuidance', sortable: false, width: '60px' } : null,
        { text: this.$t('sales.label.wpTotalPrice'), value: 'total', align: 'end', sortable: false },
        { text: this.$t('sales.label.currency'), value: 'currency', align: 'end', sortable: false },
        { text: this.$t('sales.label.variance'), value: 'variance', align: 'end', sortable: false },
        { text: this.$t('sales.label.actions'), value: 'actions', sortable: false, width: '80px' }
      ]

      return hh.filter(h => h && this.userCanAccessField(h.value))
    },
    totalHeadersIndex () {
      let total = this.computeHeaders.length - 10
      if (!this.userCanAccessField('cost')) {
        total += 1
      }
      if (!this.userCanAccessField('listPrice')) {
        total += 1
      }
      if (!this.userCanAccessField('variance')) {
        total += 1
      }
      if (!this.userCanAccessField('total')) {
        total += 1
      }
      return total
    },
    changeStateRerenderKey () {
      return this.trackerDTO.id + '_' + this.trackerDTO.state
    },
    // defaultSupplier () {
    //   // Set default supplier looking to the current workspace. The mapping is defined in configuration
    //   const workspaceId = this.workspaceId.split('.')[0]
    //   const defaultSupplierMap = this.configuration?.workpackages?.defaultSupplier

    //   let defaultSupplier
    //   if (defaultSupplierMap && defaultSupplierMap[workspaceId]) {
    //     defaultSupplier = defaultSupplierMap[workspaceId]
    //   } else {
    //     console.warn(`Workspace "${this.workspaceId}" does not have a mappping for default workpackage supplier`)
    //     defaultSupplier = defaultSupplierMap['__default']
    //   }

    //   return defaultSupplier
    // },

    wpType () {
      if (this.editedItem.wpType) return this.wpTypes.find(t => t.id === this.editedItem.wpType)
      else if (this.createMultiple.wpType) return this.wpTypes.find(t => t.id === this.createMultiple.wpType)
    },

    wpTypesMultiple () {
      return this.wpTypesAuthorized.filter(type => type.createMultiple)
    },

    formFields () { // Why computed? Because there is a piece dynamic on prop "configuration": quantity > label
      const customFields = this.wpType?.customFields || {}

      const columns = L.cloneDeep(this.configuration?.workpackages?.columns)
      const formFields = {}

      Object.keys(columns).forEach(key => {
        formFields[key] = columns[key]
        if (this.integrationFormFields[key]) {
          L.merge(formFields[key], this.integrationFormFields[key])
        }
      })
      let fields = Object.assign({}, customFields, formFields)

      return fields
    },

    integrationFormFields () { // Why computed? Because there is a piece dynamic on prop "configuration": quantity > label
      return {
        groupId: {
          combobox: this.userCanDefineNewGroups,
          options: this.getGroupOptions(this.items)
        },
        externalCost: {
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC
        },
        externalListPrice: {
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC
        },
        externalPriceItem: {
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          __pId: this.wpType?.externalPriceListId,
          disabled: this.editMode && !this.editExternalPrice,
          onEditPrice: this.editExternalPriceItem,
          onProposition: true,
          propsWhen: {
            api: (wp) => {
              const priceListId = this.wpType.externalPriceListId

              if (priceListId && this.priceListConfiguration[priceListId]) {
                const datatableConfig = this.priceListConfiguration[priceListId].dataTable.queryDatatable.columns

                // Buil the dimensions Filters
                const dimensionFilters = (this.wpType.externalDimensions || [])
                  .filter(d => d.filter !== false)
                  .map(d => {
                    const dimensionConfig = datatableConfig[d.dimension]

                    return {
                      field: dimensionConfig.dimensionFilter || d.dimension, // use priceItem dimension when no different filering field provided
                      operator: 'EQUALS',
                      value: wp && L.get(wp, d.wpPath)
                    }
                  })

                const externalSearchTerms = this.wpType.externalSearchTerms || []

                // Check all filters are fine: drop API call if 1+ is unset
                const allDimensionsSet = !dimensionFilters
                  .some(d => d.value === undefined || d.value === null)

                if (allDimensionsSet || (wp.externalPriceItem && !this.tmp['externalPriceItemEditDidOpenOnce'])) {
                  return {
                    'url': `/api/${this.applicationId}/query/findPriceAllFallback/${priceListId}`,
                    'method': 'POST',
                    'body': {
                      'filterTerms': [
                        ...dimensionFilters.filter(f => f.value)
                      ],
                      'terms': [
                        ...externalSearchTerms
                      ]
                    },
                    'path': 'data.items'
                  }
                }

                // Set this flag to mark panel did open once in editMode
                this.tmp['externalPriceItemEditDidOpenOnce'] = true
              }
              return null
            },
            // when all dimensions, except product, have a value then
            // we trigger a select change to fetch new set or price items
            isAllDimensionsSelected: (wp) => {
              return isAllObjDimensionsSelected(wp, this.wpType.externalDimensions)
            }
          }
        },
        externalSorintBu: {
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          api: this.dimensionApiConfiguration('externalSorintBu', false),
          disabled: this.editMode && !this.editExternalPrice,
          propsWhen: {
            hint: (wp) => this.externalDimensionHint('externalSorintBu', wp)
          }
        },
        externalSupplier: {
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          api: this.dimensionApiConfiguration('externalSupplier', false),
          disabled: this.editMode && !this.editExternalPrice,
          propsWhen: {
            hint: (wp) => this.externalDimensionHint('externalSupplier', wp)
          }
        },
        supplier: {
          propsWhen: {
            hint: (wp) => this.dimensionHint('supplier', wp)
          },
          disabled: this.editMode && !this.editPrice
        },
        sorintBu: {
          api: this.dimensionApiConfiguration('sorintBu'),
          propsWhen: {
            hint: (wp) => this.dimensionHint('sorintBu', wp)
          },
          disabled: this.editMode && !this.editPrice
        },
        priceItem: {
          __pId: this.wpType?.priceListId,
          disabled: this.editMode && !this.editPrice,
          onEditPrice: this.editPriceItem,
          onProposition: true,
          propsWhen: {
            // the computed prop api prepares the configuration needed by the find price api
            // to find price items, specifically it constructs the dimension filters required by the price list
            //
            // the api call is triggered every time a dimensions value is changed
            // through the watcher the FormSelect has on the fieldConfig
            api: (wp) => {
              return this.priceItemComputedApi(wp)
            },
            // when all dimensions, except product, have a value then
            // we trigger a select change to fetch new set or price items
            isAllDimensionsSelected: (wp) => {
              return isAllObjDimensionsSelected(wp, this.wpType.dimensions)
            },
            noDataText: (wp) => this.getNoDataText(wp)
          }
        },
        quantity: {
          label: this.getQuantityTitleWithType()
        },
        cost: {
          propsWhen: {
            disabled: (wp) => this.disabledCost(wp) || !DISABLED_WHEN_PRICEITEM_NOT_SET
          }
        },
        listPrice: {
          propsWhen: {
            disabled: (wp) => this.disabledCost(wp) || !DISABLED_WHEN_PRICEITEM_NOT_SET
          }
        },
        unitPrice: {
          propsWhen: {
            disabled: DISABLED_WHEN_PRICEITEM_NOT_SET
          }
        },
        areaId: {
          propsWhen: {
            hint: (wp) => this.dimensionHint('areaId', wp)
          },
          disabled: this.editMode && !this.editPrice
        },
        currency: {
          options: this.currencyOptions,
          disabled: this.disabledCurrency || !this.editedItem.priceItem,
          hint: this.currencyHint
        }
      }
    },

    createMultipleFormFields () {
      const formFields = L.cloneDeep(this.formFields)
      formFields.priceItem.itemValue = 'computedId'
      formFields.priceItem.itemText = 'wpName'
      formFields.priceItem.itemTextExpr = '$name - $productName'
      const { areaId, supplier, priceItem } = formFields
      return {
        areaId,
        supplier,
        priceItem: { ...priceItem, multiple: true }
      }
    },

    getCreateMultipleFormRows () {
      return [
        ...(this.wpType?.formFields || []) // dynamic from config
      ]
    },

    formTitle () {
      const type = this.wpType
      const newLabelKey = type.formTitleNew || `${this.applicationId}.label.wpNew`
      const editLabelKey = type.formTitleEdit || `${this.applicationId}.label.wpEdit`
      return this.editedIndex === -1 ? this.$t(newLabelKey) : this.$t(editLabelKey)
    },

    priceGuidance () {
      return this.configuration?.workpackages?.priceGuidance
    },

    reactivityTrigger () {
      let res = ''
      if (this.trackerDTO) {
        return res
      }
      return res
    },

    wpIsEditable () {
      const wpReadOnlyStates = this.configuration?.workpackages?.readOnlyStates || []
      return wpReadOnlyStates.indexOf(this.trackerDTO.state) === -1
    },

    userCanDefineNewGroups () {
      return this.userCanDoAction('newGroup')
    },

    disabledCurrency () {
      let totalItems = this.items.length
      if (!this.editedItem.id) {
        totalItems++
      }
      return totalItems > 1
    },

    workPackagesCurrency () {
      return this.items.find(item => item.currency)?.currency
    },

    showCurrencyTooltip () {
      return this.items.some(item => {
        if (item.currency) {
          return item.currency !== this.workPackagesCurrency
        }
      })
    },

    currencyOptions () {
      let options = L.cloneDeep(this.configuration?.workpackages?.columns?.currency?.options) || []
      if (this.editedItem.priceItem?.currency) {
        options = options.map((option) => {
          if (!option['exchangeRate' + this.editedItem.priceItem.currency]) {
            option.disabled = true
          }
          return option
        })
      }
      return options
    },

    currencyHint () {
      let hint = ''
      if (this.editedItem.currency && this.editedItem.priceItem?.currency && this.editedItem.currency !== this.editedItem.priceItem.currency) {
        let priceItemCurrency = this.editedItem.priceItem?.currency
        let exchangeRate = '???'
        const option = this.configuration?.workpackages?.columns.currency?.options.find(o => o['id'] === this.editedItem.currency)
        if (option) {
          exchangeRate = option['exchangeRate' + priceItemCurrency] || exchangeRate
        }
        hint = this.editedItem.currency + ' = ' + priceItemCurrency + ' x ' + exchangeRate
      }
      return hint
    }

  },

  watch: {
    dialog (val) {
      val || this.close()
    },
    dialogDelete (val) {
      val || this.closeDelete()
    },
    items: {
      deep: true,
      handler (next) {
        this.$emit('workPackagesUpdated', next)
      }
    },
    async trackerDTO (newValue, oldValue) {
      if (newValue?.id !== oldValue?.id && this.items && this.items.length > 0) {
        // re-validate items when trackerDTO is received

        this.startLoader({ id: 'work-packages3' })
        try {
          // Load the priceItems details via query
          this.priceItems = await this.fetchPriceItems(this.items)

          // Decorate items
          this.items = this.items.map(this.decorateWpData)
        } catch (error) {
          this.notifyError(error)
        }
        this.stopLoader({ id: 'work-packages3' })
      }
    }
  },

  created () {
    this.fetchWorkPackage()
  },

  mounted: function () {
    this.$debug('TrackerDataTableItemExtend mounted')
    this.setGroupingConfiguration(this.configuration?.workpackages)
  },

  methods: {
    ...mapActions({
      notifyError: 'notificationStore/notifyError',
      notifyMessage: 'notificationStore/notifyMessage',
      startLoader: 'loaderStore/startLoader',
      stopLoader: 'loaderStore/stopLoader'
    }),
    decorateWpData (wp) {
      const w = Object.assign({}, this.defaultItem, wp)
      if (w.externalPriceItem) w.externalWorkpackage = true // set flag for checkbox when external product is set

      // Match the wpType looking to the selected product
      try {
        const wpType = this.matchWpType(wp)
        w.wpType = wpType.id
        const priceItem = this.priceItems[wp.priceItem.id]
        const priceItemProps = this.formFields.priceItem?.outValueExpr
        priceItemProps && typeof priceItemProps === 'object' && Object.keys(priceItemProps).forEach(key => {
          if (!w.priceItem[key] && priceItem[key] && (L.isString(priceItem[key]) || L.isNumber(priceItem[key]))) {
            Vue.set(w.priceItem, key, priceItem[key])
          }
        })
        // Decorate wp with the JOIN on the priceItems dimensions
        if (wpType.dimensions) {
          wpType.dimensions.map(d => {
            const tmp = d.wpPath.split('.')
            const value = priceItem[d.dimension]
            if (!w[tmp[0]]) {
              Vue.set(w, tmp[0], value)
            }
          })
        }

        if (wpType.externalDimensions && wp.externalPriceItem) {
          const externalPriceItem = this.priceItems[wp.externalPriceItem.id]
          const externalPriceItemProps = this.formFields.externalPriceItem?.outValueExpr
          externalPriceItemProps && typeof externalPriceItemProps === 'object' && Object.keys(externalPriceItemProps).forEach(key => {
            if (!w.externalPriceItem[key] && externalPriceItem[key] && (L.isString(externalPriceItem[key]) || L.isNumber(externalPriceItem[key]))) {
              Vue.set(w.externalPriceItem, key, externalPriceItem[key])
            }
          })
          wpType.externalDimensions.map(d => {
            const tmp = d.wpPath.split('.')
            const value = externalPriceItem[d.dimension]
            if (!w[tmp[0]]) {
              Vue.set(w, tmp[0], value)
            }
          })
        }

        w.groupId = w.groupId ? this.groupName(w.groupId) : this.defaultGroupId(w) // decorate to default groupId when unset
      } catch (e) {
        w.invalid = true
        this.notifyMessage(this.$t('message.wpDisabled'))
      }

      if (typeof w.tags === 'string') {
        w.tags = Configuration.datatable.parseTagsString(w.tags)
      }

      return w
    },

    getGroupOptions (items) {
      const ids = L.uniq([
        ...items.map(wp => this.groupName(wp.groupId)),
        this.defaultGroupId(this.editedItem)
      ])

      const options = ids.map(id => ({
        id,
        text: this.groupName(id)
      }))
      return options
    },

    async fetchPriceItems (workpackages) {
      // Identify the priceItems to be loaded, grouped by priceListId
      const priceLists = workpackages.reduce((acc, wp) => {
        if (wp.priceItem) {
          acc[wp.priceItem.priceListId] = acc[wp.priceItem.priceListId]
            ? [...acc[wp.priceItem.priceListId], wp.priceItem.id]
            : [wp.priceItem.id]
        }
        if (wp.externalPriceItem) {
          acc[wp.externalPriceItem.priceListId] = acc[wp.externalPriceItem.priceListId]
            ? [...acc[wp.externalPriceItem.priceListId], wp.externalPriceItem.id]
            : [wp.externalPriceItem.id]
        }

        return acc
      }, {})

      const priceItems = {}
      try {
        // Invoke 1 query API for each priceListId filtering on the ids to load
        const responses = await Promise.all(Object.entries(priceLists).map(([priceListId, priceItemIds]) => {
          const priceListQuery = this.priceListConfiguration[priceListId]?.dataTable.querySelectMode
          if (priceListQuery) {
            const url = `/api/${this.applicationId}/query/${priceListQuery}`
            const queryPlaceholders = this.configuration?.workpackages?.priceItemQueryPlaceholders || {}

            const body = {
              'filterTerms': [
                { field: 'priceListId', operator: 'EQUALS', value: priceListId }, // inject priceListId filter
                { field: 'id', operator: 'IN', values: priceItemIds } // inject filter on the priceItems ites to load
              ],
              placeHolders: queryPlaceholders
            }
            return this.$axios.post(url, body, this.$axios.agilisRequestConfig(url))
          }
          return Promise.resolve({})
        }))

        // Prepare key-value map of priceItems
        responses.forEach((response) => {
          const items = response?.data?.items
          items && Object.values(items).forEach((priceItem) => {
            priceItems[priceItem.id] = priceItem
          })
        })
      } catch (error) {
        this.notifyError(error)
      }

      return priceItems
    },

    async fetchWorkPackage () {
      this.startLoader({ id: 'work-packages3' })

      try {
        // Load workpackages
        const url = this.agilisUrlWorkPackage(this.applicationId, this.trackerTypeId, this.trackerId)
        const response = await this.$axios.get(url, this.$axios.agilisRequestConfig(url))
        const data = response.data

        // Load the priceItems details via query
        this.priceItems = await this.fetchPriceItems(data)

        // Decorate items
        this.items = data.map((wp) => this.decorateWpData(wp))
      } catch (error) {
        this.notifyError(error)
      }
      this.stopLoader({ id: 'work-packages3' })
    },

    formatUnitOfMeasure (value) {
      switch (value) {
        case 'day':
          return 0
        case 'hour':
          return 1
        case 'unit':
          return 2
        default:
          return 0
      }
    },

    formatCurrency (value) {
      if (value === null || value === undefined || value === '') return '\xA0'
      return value.toLocaleString(i18n.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    createItem (wpType) {
      this.editMode = false
      const startDate = this.trackerDTO.startDate || null
      const endDate = this.trackerDTO.endDate || null

      this.editedItem = Object.assign({}, this.defaultItem, {
        startDate,
        endDate,
        wpType: wpType
      })

      // Add custom default values and meke them reactive
      const defaultsFromType = this.wpType.defaultValues || {}
      Object.entries(defaultsFromType).forEach(([k, v]) => {
        Vue.set(this.editedItem, k, v)
      })

      this.editedIndex = -1
      this.dialog = true
      this.dialogReadOnly = false
      this.formRows = this.getFormFields(wpType)
    },

    copyItem (item) {
      this.editMode = false
      const newItem = Object.assign(
        {},
        this.defaultItem,
        L.pickBy(item, (value, key) => key !== 'id') // remove id: on save will create a new one
      )

      this.editedItem = newItem
      this.editedIndex = -1
      this.dialog = true
      this.dialogReadOnly = false

      this.formRows = this.getFormFields(item.wpType)

      // Edit flow: force form immediate validation
      this.forceFormValidation()
    },

    editItem (item) {
      this.editMode = true
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, this.defaultItem, item)
      this.dialog = true
      this.dialogReadOnly = false

      this.formRows = this.getFormFields(item.wpType)

      // Edit flow: force form immediate validation
      this.forceFormValidation()
    },

    viewItem (item) {
      this.editMode = true
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, this.defaultItem, item)
      this.dialog = true
      this.dialogReadOnly = true

      this.formRows = this.getFormFields(item.wpType)

      // Edit flow: force form immediate validation
      this.forceFormValidation()
    },

    deleteItem (item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialogDelete = true
    },

    deleteItemConfirm (item) {
      this.startLoader({ id: 'work-packages3' })
      if (Array.isArray(item.tags)) {
        item.tags = Configuration.datatable.stringifyTag(item.tags)
      }
      const url = this.agilisUrlWorkPackageEdit(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        item.id)
      this.$axios.delete(
        url,
        {
          ...this.$axios.agilisRequestConfig(url),
          data: item
        })
        .then((response) => {
          this.fetchWorkPackage()
        })
        .catch((error) => {
          this.notifyError(error)
          this.stopLoader({ id: 'work-packages3' })
        })
      this.dialogDelete = false
      this.closeDelete()
    },

    close () {
      this.dialog = false
      this.editMode = false
      this.editPrice = false
      this.editExternalPrice = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
        this.tmp = {} // reset temp variables
      })
    },

    closeDelete () {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    forceFormValidation () {
      setTimeout(() => {
        this.$refs.formValidationObserver.validate()
      }, 0)
    },

    async saveWP (_item) {
      this.startLoader({ id: 'save-work-package' })

      setTimeout(async () => {
        let item = JSON.parse(JSON.stringify(_item)) // work on a copy of the object to persist data in case of errors

        // CLean up priceItem & external PriceItem
        if (item.priceItem) item.priceItem = { id: item.priceItem.id, isNew: !!item.priceItem.isNew }
        if (item.externalPriceItem) item.externalPriceItem = { id: item.externalPriceItem.id, isNew: !!item.externalPriceItem.isNew }

        // Clean up wp model to prevent inconsitent values
        if (item.externalWorkpackage === false) {
          delete item.externalCost
          delete item.externalListPrice
          delete item.externalPriceItem
          delete item.externalSorintBu
          delete item.externalSupplier
        }
        delete item.externalWorkpackage
        // ///////////////////////////////////////////////

        if (Array.isArray(item.tags)) {
          item.tags = Configuration.datatable.stringifyTag(item.tags)
        }

        this.startLoader({ id: 'save-work-package' })
        if (item.id) {
          const url = this.agilisUrlWorkPackageEdit(
            this.applicationId,
            this.trackerTypeId,
            this.trackerId,
            item.id)

          try {
            await this.$axios.put(url, item, this.$axios.agilisRequestConfig(url))
            this.fetchWorkPackage()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-work-package' })
          this.close()
        } else {
          item.propositionId = this.trackerId
          const url = this.agilisUrlWorkPackage(
            this.applicationId,
            this.trackerTypeId,
            this.trackerId)
          try {
            await this.$axios.post(url, item, this.$axios.agilisRequestConfig(url))
            this.fetchWorkPackage()
          } catch (error) {
            this.notifyError(error)
          }
          this.stopLoader({ id: 'save-work-package' })
          this.close()
        }
      }, 0)
    },

    agilisUrlWorkPackage (applicationId, trackerTypeId, trackerId) {
      return `/api/${applicationId}/tracker/${trackerTypeId}/${trackerId}/workpackage3`
    },

    agilisUrlWorkPackageMultiple (applicationId, trackerTypeId, trackerId) {
      return `/api/${applicationId}/tracker/${trackerTypeId}/${trackerId}/workpackage3/many`
    },

    agilisUrlWorkPackageEdit (applicationId, trackerTypeId, trackerId, workPackageId) {
      return `/api/${applicationId}/tracker/${trackerTypeId}/${trackerId}/workpackage3/${workPackageId}`
    },

    handlePriceItemUpdate (delta) {
      const originalPriceItemId = this.editedItem.priceItem?.originalPriceItemId || this.editedItem.priceItem?.id
      if (!this.editedItem.priceItem || originalPriceItemId !== delta.priceItem.id) {
        const listPrice = delta.priceItem?.listPrice || null
        const cost = delta.priceItem?.cost || null
        // Retrieve scale factor from JSON config
        const jobType = this.editedItem.jobType
        const scaleFactor = getPriceScaleFactor(delta.priceItem, jobType)

        // Update the model
        delta.currency = this.workPackagesCurrency || this.editedItem.currency || delta.priceItem.currency
        delta.cost = this.convertCurrency(cost, delta.currency, delta.priceItem.currency)
        delta.listPrice = this.convertCurrency((listPrice * scaleFactor).toFixed(2), delta.currency, delta.priceItem.currency)
        delta.unitPrice = delta.listPrice

        if (delta.priceItem?.defaultQuantity) {
          delta.quantity = delta.priceItem?.defaultQuantity
        }
        if (delta.priceItem?.defaultTitle) {
          delta.title = delta.priceItem?.defaultTitle
        }
        // Update unit of measure according to product
        if (delta.priceItem.productUnitOfMeasure) {
          delta.unitOfMeasure = this.formatUnitOfMeasure(delta.priceItem.productUnitOfMeasure)
        }
        if (this.editedItem.externalPriceItem) {
          const externalCost = this.editedItem.externalCost
          const externalListPrice = this.editedItem.externalListPrice
          if (externalCost) delta.cost = externalCost
          // Update the listPrice when the external product is selected and external listPrice is > editedItem listPrice
          if (externalListPrice && parseFloat(externalListPrice) > parseFloat(delta.listPrice) && this.editedItem.wpType !== 'wpTypeInternal') {
            delta.listPrice = externalListPrice
          }
        }
        // Update the groupId if not set
        if (!this.editedItem.groupId) {
          delta.groupId = this.defaultGroupId({
            ...this.editedItem,
            ...delta
          })
        }
      }
      return delta
    },

    handleJobTypeUpdate (delta) {
      // Retrieve scale factor from JSON config
      const jobType = delta.jobType
      const scaleFactor = getPriceScaleFactor(this.editedItem.priceItem, jobType)
      delta.cost = this.convertCurrency(this.editedItem.priceItem.cost, this.editedItem.currency, this.editedItem.priceItem.currency)
      delta.listPrice = this.convertCurrency((this.editedItem.priceItem.listPrice * scaleFactor).toFixed(2), this.editedItem.currency, this.editedItem.priceItem.currency)
      delta.unitPrice = delta.listPrice

      return delta
    },

    handleExternalPriceItemUpdate (delta) {
      const originalExternalPriceItemId = this.editedItem.externalPriceItem?.originalPriceItemId || this.editedItem.externalPriceItem?.id
      if (!this.editedItem.externalPriceItem || originalExternalPriceItemId !== delta.externalPriceItem.id) {
        let externalCost = delta.externalPriceItem.cost
        let externalListPrice = delta.externalPriceItem.listPrice
        if (this.editedItem.currency) {
          externalCost = this.convertCurrency(externalCost, this.editedItem.currency, delta.externalPriceItem.currency)
          externalListPrice = this.convertCurrency(externalListPrice, this.editedItem.currency, delta.externalPriceItem.currency)
        }
        delta.externalCost = externalCost
        delta.externalListPrice = externalListPrice
        if (this.editedItem.priceItem) {
          if (externalCost) delta.cost = externalCost
          if (externalListPrice && parseFloat(externalListPrice) > parseFloat(this.editedItem.listPrice) && this.editedItem.wpType !== 'wpTypeInternal') {
            delta.listPrice = externalListPrice
          }
        }
      }
      return delta
    },

    handleExternalWorkpackageUpdate (delta) {
      // Change of supplier: reset costs & product
      if (this.editedItem.priceItem && this.editedItem.externalPriceItem) {
        const scaleFactor = getPriceScaleFactor(this.editedItem.priceItem, this.editedItem.jobType)
        delta.cost = this.convertCurrency(this.editedItem.priceItem.cost, this.editedItem.currency, this.editedItem.priceItem.currency)
        delta.listPrice = (this.convertCurrency(this.editedItem.priceItem.listPrice, this.editedItem.currency, this.editedItem.priceItem.currency) * scaleFactor).toFixed(2)
        delta.unitPrice = delta.listPrice
      }
      delta.externalCost = null
      delta.externalListPrice = null
      delta.externalPriceItem = null
      return delta
    },

    handleCurrencyUpdate (delta) {
      if (!delta.priceItem && this.editedItem.listPrice) {
        const jobType = this.editedItem.jobType
        const scaleFactor = getPriceScaleFactor(this.editedItem.priceItem, jobType)
        delta.cost = this.convertCurrency(this.editedItem.priceItem.cost, delta.currency, this.editedItem.priceItem.currency)
        delta.listPrice = (this.convertCurrency(this.editedItem.priceItem.listPrice, delta.currency, this.editedItem.priceItem.currency) * scaleFactor).toFixed(2)
        delta.unitPrice = delta.listPrice

        if (this.editedItem.externalPriceItem) {
          delta.externalCost = this.convertCurrency(this.editedItem.externalPriceItem.cost, delta.currency, this.editedItem.priceItem.currency)
          delta.externalListPrice = this.convertCurrency(this.editedItem.externalPriceItem.listPrice, delta.currency, this.editedItem.priceItem.currency)
        }
      }
      return delta
    },

    handleCostUpdate (delta) {
      if (!delta.priceItem && !delta.currency && !delta.jobType && !delta.externalPriceItem && delta.externalWorkpackage === undefined) {
        const jobType = this.editedItem.jobType
        const scaleFactor = getPriceScaleFactor(this.editedItem.priceItem, jobType)
        delta.listPrice = ((+delta.cost + (+delta.cost / 100) * 10) * scaleFactor).toFixed(2)
        delta.unitPrice = delta.listPrice
      }
      return delta
    },

    beforeChange (delta) {
      if (delta.priceItem !== undefined) { // PRICE ITEM updated
        delta = this.handlePriceItemUpdate(delta)
      }
      if (delta.jobType !== undefined && this.editedItem.listPrice) { // JOB TYPE updated
        delta = this.handleJobTypeUpdate(delta)
      }
      if (delta.externalPriceItem !== undefined) { // External product updated: update exteral[cost, listPrice, UnitPrice]
        delta = this.handleExternalPriceItemUpdate(delta)
      }
      if (delta.externalWorkpackage !== undefined) {
        delta = this.handleExternalWorkpackageUpdate(delta)
      }
      if (delta.currency !== undefined) { // Currency update
        delta = this.handleCurrencyUpdate(delta)
      }
      if (delta.cost) {
        delta = this.handleCostUpdate(delta)
      }
      if (delta.externalCost && this.editedItem.priceItem && !delta.priceItem && !delta.externalPriceItem) {
        delta.cost = delta.externalCost
      }
      if (delta.externalListPrice && this.editedItem.priceItem && !delta.priceItem && !delta.externalPriceItem) {
        if (parseFloat(delta.externalListPrice) > parseFloat(this.editedItem.listPrice) && this.editedItem.wpType !== 'wpTypeInternal') {
          delta.listPrice = delta.externalListPrice
        }
      }
      if (delta.supplier) {
        delta.areaId = null
        delta.priceItem = null
      }

      return delta
    },

    getQuantityTitleWithType () {
      let quantityLabel = 'sales.label.wpQuantityDays'
      for (const option of this.configuration.columns.type.options) {
        if (option.id === this.trackerDTO.type && option.wpQuantityLabel) {
          quantityLabel = option.wpQuantityLabel
          break
        }
      }
      return quantityLabel
    },

    userCanDoHttp (operation) {
      const tmp = Configuration.auth.userCanAccessFeature(this.selectedContext, 'workpackage3', operation, this.trackerDTO)
      return tmp
    },

    userCanDoAction (buttonId) {
      const feature = `workpackage3/datatable/${buttonId}`
      const tmp = Configuration.auth.userCanAccessFeature(this.selectedContext, feature, null, this.trackerDTO)
      return tmp
    },

    userCanDataTableAction (operation) {
      let tmp
      const wpEditable = this.wpIsEditable

      switch (operation) {
        case 'view':
          tmp = this.userCanDoAction('view') ||
          (!wpEditable && this.userCanDataTableAction('edit')) // fallback to view when user canEdit but wp is not editable
          break

        case 'create':
          tmp = this.userCanDoHttp('post') && this.userCanDoAction('create') && wpEditable
          break

        case 'copy':
          tmp = this.userCanDoHttp('post') && this.userCanDoAction('copy') && wpEditable
          break

        case 'delete':
          tmp = this.userCanDoHttp('delete') && this.userCanDoAction('delete') && wpEditable
          break

        case 'edit':
          tmp = this.userCanDoHttp('put') && this.userCanDoAction('edit') && wpEditable
          break

        case 'editInline':
          tmp = this.userCanDoHttp('put') && this.userCanDoAction('editInline') && wpEditable

          break

        default:
          break
      }

      return tmp
    },

    fieldTitle (required, label) {
      return Configuration.datatable.getRequiredLabel({ required, label })
    },

    renderJobType (jobType) {
      const opt = this.formFields.jobType.options.find(j => j.id === jobType)
      if (opt) return this.$t(opt.text)
    },

    renderTags (tags) {
      return Array.isArray(tags) ? tags.join(', ') : tags
    },

    priceMarginColor (item) {
      const priceGuidanceLogic = this.priceGuidance.jsonLogic
      const defaultColor = 'transparent'

      if (priceGuidanceLogic) {
        try {
          const jlContext = {
            cost: item.cost,
            listPrice: item.listPrice,
            unitPrice: item.unitPrice,

            externalWorkpackage: !!item.externalWorkpackage,
            externalCost: item.externalCost,
            externalListPrice: item.externalListPrice,

            quantity: item.quantity,
            totalPrice: item.quantity ? (item.quantity * item.unitPrice) : item.unitPrice
          }
          const logicOutput = jsonLogic.apply(priceGuidanceLogic, jlContext)
          return logicOutput || defaultColor
        } catch (e) {
          console.debug('Invalid jsonLogic', e)
        }
        return defaultColor
      }
    },

    userCanAccessField (colId) {
      return colId === 'actions' ||
        Configuration.auth.userCanAccessField(this.selectedContext, colId, 'workpackage3')
    },

    userCanEditField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId, 'workpackage3', 'edit')
    },

    async inlinePriceEdit (item, newPrice) {
      if (parseFloat(item.unitPrice) !== parseFloat(newPrice)) {
        const delta = { unitPrice: newPrice }

        const wpUpdated = Object.assign({}, item, delta)
        await this.saveWP(wpUpdated)
      }
    },

    wpTypeIcon (wp) {
      return wp && wp.icon
    },

    convertCurrency (value, targetCurrency, originCurrency) {
      let colConfig = this.formFields.currency
      let exchangeRate = 1
      if (colConfig.options) { // options defined in config
        const option = colConfig.options.find(o => o['id'] === targetCurrency)
        if (option) {
          exchangeRate = option['exchangeRate' + originCurrency] || exchangeRate
        }
      }
      return (value * exchangeRate).toFixed(2)
    },

    getFormFields (wpType) {
      let workPackageTypes = this.wpTypes
      let wpTypeConfig = workPackageTypes.find(price => { return price.id === wpType })

      let externalProductFields = []
      if (wpTypeConfig.externalPriceListId) { // only when there is an externalPriceListId
        externalProductFields = [
          { cols: ['externalWorkpackage'] },
          ...(wpTypeConfig?.externalFormFields || []), // dynamic from config
          { cols: ['externalCost', 'externalListPrice'] }
        ]
      }

      return [
        { cols: ['title'] },
        { cols: ['description'] },
        { cols: ['tags'] },
        ...(wpTypeConfig?.formFields || []), // dynamic from config
        ...externalProductFields
      ]
    },

    groupMarginColor (group) {
      const stats = this.getGroupData(group)

      if (stats) {
        stats.unitPrice = stats.total
        return this.priceMarginColor(stats)
      }
    },

    dimensionHint (field, wp) {
      try {
        const priceItem = wp.priceItem
        const _d = this.wpType.dimensions.find(d => d.wpPath.split('.')[0] === field)
        const dimension = _d && _d.dimension

        if (this.dialog && priceItem && dimension && !priceItem[dimension] && !wp[_d.wpPath]) {
          return this.$t('sales.label.wpDimensionNotMatch')
        }
      } catch (e) {
        // let it go
      }
    },

    externalDimensionHint (field, wp) {
      try {
        const priceItem = wp.externalPriceItem
        const _d = this.wpType.externalDimensions.find(d => d.wpPath.split('.')[0] === field)
        const dimension = _d && _d.dimension

        if (this.dialog && priceItem && dimension && !priceItem[dimension]) {
          return this.$t('sales.label.wpDimensionNotMatch')
        }
      } catch (e) {
        // let it go
      }
    },

    dimensionApiConfiguration (field, internal = true) {
      const priceListId = internal ? this.wpType?.priceListId : this.wpType?.externalPriceListId
      const dimensions = internal ? this.wpType?.dimensions : this.wpType?.externalDimensions

      const _d = dimensions?.find(d => d.wpPath.split('.')[0] === field)
      const dimension = _d && _d.dimension

      // Read query from config or fallback to default query name given the dimension
      const query = dimension && (dimension.distinctQuery || `priceListDistinct${dimension.charAt(0).toUpperCase() + dimension.slice(1)}`)

      return query && {
        'url': `/api/${this.applicationId}/query/${query}`,
        'method': 'POST',
        'body': {
          'filterTerms': [
            { field: 'priceListId', operator: 'IN', values: this.getPriceListChain(priceListId) }
          ]
        },
        'path': 'data.items'
      }
    },

    getPriceListChain (priceListId) {
      const priceListConfiguration = this.priceListConfiguration
      const chain = [priceListId]

      let tmp = priceListId
      let t = priceListConfiguration[priceListId]

      while (tmp && t && t.fallback) {
        const fallbackId = t.fallback
        chain.push(fallbackId)

        // Next step in loop
        t = priceListConfiguration[fallbackId]
        tmp = fallbackId
      }

      return chain
    },

    priceListName (item) {
      const priceListId = item.priceListId
      const priceList = this.priceListConfiguration[priceListId]
      return priceList?.name && this.$t(priceList.name)
    },

    priceListDetails (item) {
      const wpDimensions = this.wpTypes.find(t => t.id === item.wpType)?.dimensions
      return wpDimensions && wpDimensions.map(d => {
        if (d.hint !== false) {
          if (!d.dimension.includes('Str')) {
            const label = this.$t(d.hintLabel) || this.$t(this.formFields[d.wpPath.split('.')[0]].label)
            const value = d.hintPath ? L.get(item, d.hintPath) : item[d.wpPath.split('.')[0]]?.title ? item[d.wpPath.split('.')[0]].title : ''
            return `${label} : ${value}`
          } else {
            if (item[d.hintPath] || item[d.wpPath]) {
              const l = this.$t(this.formFields[d.wpPath].label)
              let v = this.dimensionValueToString(d, item[d.hintPath] || item[d.wpPath])

              return l + ': ' + v
            }
          }
        }
      }).filter(d => d).join('\n')
    },

    // ////////////////////////////////////////////////////////////////
    // Create multiple workpackages
    // ////////////////////////////////////////////////////////////////
    closeMultiple () {
      this.dialogMultiple = false
    },

    async createMultipleWP () {
      const defaultsFromType = this.wpType.defaultValues || {}
      const startDate = this.trackerDTO.startDate || null
      const endDate = this.trackerDTO.endDate || null

      const base = Object.assign({}, this.defaultItem, defaultsFromType, {
        startDate,
        endDate,
        sequence: 1,
        jobType: 'Lavorativo',
        unitOfMeasure: 0,
        propositionId: this.trackerId,
        wpType: this.wpType.id,
        tags: ''
      })
      const wpsArray = this.createMultiple.priceItem.map((priceItem) => {
        const currency = this.workPackagesCurrency || priceItem.currency
        const wpPriceItem = {
          ...base,
          // PriceItem-dependant fields
          title: priceItem.name || priceItem.defaultTitle || priceItem.productName,
          priceItem: { id: priceItem.id, dimension3IdStr: priceItem.dimension3IdStr },
          areaId: this.createMultiple.areaId,
          areaName: this.createMultiple.areaNode.name,
          unitOfMeasure: this.formatUnitOfMeasure(priceItem.dimension1Id.unitOfMeasure),
          quantity: priceItem.quantity || priceItem.defaultQuantity || 1,
          unitPrice: this.convertCurrency(priceItem.listPrice, currency, priceItem.currency),
          listPrice: this.convertCurrency(priceItem.listPrice, currency, priceItem.currency),
          cost: this.convertCurrency(priceItem.cost, currency, priceItem.currency),
          currency: currency
        }
        return { ...wpPriceItem, groupId: this.defaultGroupId(wpPriceItem) }
      })

      // Call multiple creation API
      const url = this.agilisUrlWorkPackageMultiple(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)
      try {
        await this.$axios.post(url, wpsArray, this.$axios.agilisRequestConfig(url))
        this.fetchWorkPackage()

        this.createMultiple = {}
        this.closeMultiple()
      } catch (error) {
        this.notifyError(error)
      }
      this.stopLoader({ id: 'save-work-package' })
    },

    onCreateMultipleItems (wpType) {
      this.editMode = false
      this.createMultiple = { // init model
        wpType: wpType.id,
        priceItem: null,
        supplier: null,
        areaId: null,
        areaNode: null
      }

      // Build form dynamically
      this.createMultipleFormRows = this.getCreateMultipleFormRows

      // Open the dialog
      this.dialogMultiple = true
    },

    editPriceItem () {
      this.editPrice = true
    },

    editExternalPriceItem () {
      this.editExternalPrice = true
    },

    priceItemComputedApi (wp) {
      const priceListId = this.wpType.priceListId
      if (priceListId && this.priceListConfiguration[priceListId]) {
        const datatableConfig = this.priceListConfiguration[priceListId].dataTable.queryDatatable.columns

        // Buil the dimensions Filters
        const dimensionFilters = this.wpType.dimensions
          .filter(d => d.filter !== false)
          .map(d => {
            const dimensionConfig = datatableConfig[d.dimension]

            return {
              field: dimensionConfig.dimensionFilter || d.dimension, // use priceItem dimension when no different filering field provided
              operator: 'EQUALS',
              value: (wp && L.get(wp, d.wpPath)) || null
            }
          })

        const searchTerms = this.wpType.searchTerms || []

        // Check all filters are fine: drop API call if 1+ is unset
        // const allDimensionsSet = !dimensionFilters.some(d => d.value === undefined || d.value === null)

        if (this.searchPriceItem(dimensionFilters) || (wp.priceItem && !this.tmp['priceItemEditDidOpenOnce'])) {
          // Search priceItem filtering on dimensions
          return {
            'url': `/api/${this.applicationId}/query/findPriceAllFallback/${priceListId}`,
            'method': 'POST',
            'body': {
              'filterTerms': [
                ...dimensionFilters
              ],
              'terms': [
                ...searchTerms
              ]
            },
            'path': 'data.items'
          }
        }

        // Set this flag to mark panel did open once in editMode
        this.tmp['priceItemEditDidOpenOnce'] = true
      }
    },

    searchPriceItem (dimensions = null) {
      return !!dimensions.filter(d => d.value).length
    },

    getNoDataText (wp) {
      if (wp.wpType === 'wpType2') {
        return wp.supplier ? 'label.noDataTextProduct' : 'label.noDataTextPriceItem'
      } else {
        return 'label.noDataTextPriceItemExternal'
      }
    },

    disabledCost (wp) {
      const costEditable = this.wpTypes.find(t => t.id === wp.wpType).editableProductTypes || []
      return !costEditable.includes(wp.priceItem?.productType)
    },

    formUpdate (newValue) {
      if (this.dialogMultiple) {
        this.createMultiple.areaNode = newValue
      } else {
        this.editedItem.areaName = newValue?.name
      }
    }
    // ////////////////////////////////////////////////////////////////
  }
}
